import { useCallback, useEffect, useState, useRef } from 'react';
import { useUnityContext } from 'react-unity-webgl';
import { AppearanceSettings, ColorRGBA } from '../types/appearance';
import { CharacterClass } from '../types/character';
import { devLog } from '../utils/logger';

interface UseUnityCharacterPreviewOptions {
  loaderUrl: string;
  dataUrl: string;
  frameworkUrl: string;
  codeUrl: string;
  // Required for Addressables (remote gestures): the local catalog lives under
  // <streamingAssetsUrl>/aa — without this Unity resolves "StreamingAssets"
  // relative to the page origin and every remote-gesture load fails.
  streamingAssetsUrl?: string;
  companyName?: string;
  productName?: string;
  productVersion?: string;
}

interface UseUnityCharacterPreviewReturn {
  unityProvider: ReturnType<typeof useUnityContext>['unityProvider'];
  isLoaded: boolean;
  isReady: boolean;
  loadingProgression: number;
  setGender: (gender: 'male' | 'female') => void;
  setCharacterClass: (characterClass: CharacterClass) => void;
  setOutfit: (outfitIndex: number) => void;
  setHat: (hatIndex: number) => void;
  setGlasses: (glassesIndex: number) => void;
  setBackpack: (backpackIndex: number) => void;
  setFacialHair: (styleIndex: number) => void;
  setPart: (slot: string, index: number) => void;
  setMorph: (key: string, value: number) => void;
  setCameraView: (view: 'face' | 'body') => void;
  setPreviewRotation: (degrees: number) => void;
  updateSkinColor: (color: ColorRGBA) => void;
  updateHairStyle: (styleIndex: number) => void;
  updateHairColor: (color: ColorRGBA) => void;
  updateEyeColor: (color: ColorRGBA) => void;
  loadFullAppearance: (appearance: AppearanceSettings) => void;
  setCharacterType: (characterClass: CharacterClass, gender: 'male' | 'female') => void;
  playAnimation: (animation: string) => void;
  setAnimationSpeed: (speed: number) => void;
  resetToIdle: () => void;
  startShowcase: () => void;
  stopShowcase: () => void;
}

export function useUnityCharacterPreview(
  options: UseUnityCharacterPreviewOptions
): UseUnityCharacterPreviewReturn {
  const [isReady, setIsReady] = useState(false);
  const pendingMessages = useRef<Array<{ method: string; arg: string }>>([]);
  const modeSentRef = useRef(false);

  const {
    unityProvider,
    isLoaded,
    loadingProgression,
    sendMessage,
  } = useUnityContext({
    loaderUrl: options.loaderUrl,
    dataUrl: options.dataUrl,
    frameworkUrl: options.frameworkUrl,
    codeUrl: options.codeUrl,
    streamingAssetsUrl: options.streamingAssetsUrl,
    companyName: options.companyName || 'GameDate',
    productName: options.productName || 'CharacterEditor',
    productVersion: options.productVersion || '1.0',
    autoSyncPersistentDataPath: true,
  });

  // Listen for Unity router ready event and send character_editor mode.
  // NOTE: unity-router-ready can fire during Unity boot, BEFORE react-unity-webgl
  // marks isLoaded — sendMessage is silently swallowed then ("Unable to Send
  // Message while Unity is not Instantiated"). Only send (and latch modeSentRef)
  // when the instance is actually loaded; otherwise leave the latch unset so the
  // isLoaded fallback below delivers the mode.
  const isLoadedRef = useRef(isLoaded);
  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);
  useEffect(() => {
    const handleRouterReady = () => {
      devLog('[useUnityCharacterPreview] Router ready event received, modeSentRef:', modeSentRef.current, 'isLoaded:', isLoadedRef.current);
      if (!isLoadedRef.current) {
        devLog('[useUnityCharacterPreview] Router ready before Unity instantiated — deferring to isLoaded fallback');
        return;
      }
      if (!modeSentRef.current) {
        devLog('[useUnityCharacterPreview] >>> SENDING character_editor mode to UnifiedSceneRouter');
        sendMessage('UnifiedSceneRouter', 'SetAppMode', 'character_editor');
        modeSentRef.current = true;
        devLog('[useUnityCharacterPreview] Mode sent successfully');
      }
    };

    window.addEventListener('unity-router-ready', handleRouterReady);
    return () => {
      window.removeEventListener('unity-router-ready', handleRouterReady);
    };
  }, [sendMessage]);

  // Also try to send mode when Unity is loaded (fallback for if router ready event was missed)
  useEffect(() => {
    devLog('[useUnityCharacterPreview] isLoaded changed:', isLoaded, 'modeSentRef:', modeSentRef.current);
    if (isLoaded && !modeSentRef.current) {
      // Small delay to ensure Unity is fully initialized
      devLog('[useUnityCharacterPreview] Setting up 500ms fallback timer');
      const timeoutId = setTimeout(() => {
        devLog('[useUnityCharacterPreview] Fallback timer fired, modeSentRef:', modeSentRef.current);
        if (!modeSentRef.current) {
          devLog('[useUnityCharacterPreview] >>> SENDING character_editor mode (FALLBACK)');
          sendMessage('UnifiedSceneRouter', 'SetAppMode', 'character_editor');
          modeSentRef.current = true;
          devLog('[useUnityCharacterPreview] Fallback mode sent successfully');
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoaded, sendMessage]);

  // Listen for Unity preview ready event
  useEffect(() => {
    const handlePreviewReady = () => {
      devLog('[useUnityCharacterPreview] Preview ready');
      setIsReady(true);

      // Send any pending messages
      pendingMessages.current.forEach(({ method, arg }) => {
        sendMessage('CharacterPreviewBridge', method, arg);
      });
      pendingMessages.current = [];
    };

    window.addEventListener('unity-preview-ready', handlePreviewReady);
    return () => {
      window.removeEventListener('unity-preview-ready', handlePreviewReady);
    };
  }, [sendMessage]);

  // Helper to send message, queueing if not ready
  const sendBridgeMessage = useCallback(
    (method: string, arg: string) => {
      if (isReady) {
        sendMessage('CharacterPreviewBridge', method, arg);
      } else {
        pendingMessages.current.push({ method, arg });
      }
    },
    [isReady, sendMessage]
  );

  const setGender = useCallback(
    (gender: 'male' | 'female') => {
      sendBridgeMessage('SetGender', gender);
    },
    [sendBridgeMessage]
  );

  const setCharacterClass = useCallback(
    (characterClass: CharacterClass) => {
      const classIndex =
        characterClass === 'tank'
          ? 0
          : characterClass === 'archer'
          ? 1
          : characterClass === 'mage'
          ? 2
          : 3;
      sendBridgeMessage('SetCharacterClass', String(classIndex));
    },
    [sendBridgeMessage]
  );

  const updateSkinColor = useCallback(
    (color: ColorRGBA) => {
      sendBridgeMessage('UpdateSkinColor', JSON.stringify(color));
    },
    [sendBridgeMessage]
  );

  const updateHairStyle = useCallback(
    (styleIndex: number) => {
      sendBridgeMessage('UpdateHairStyle', String(styleIndex));
    },
    [sendBridgeMessage]
  );

  const updateHairColor = useCallback(
    (color: ColorRGBA) => {
      sendBridgeMessage('UpdateHairColor', JSON.stringify(color));
    },
    [sendBridgeMessage]
  );

  const updateEyeColor = useCallback(
    (color: ColorRGBA) => {
      sendBridgeMessage('UpdateEyeColor', JSON.stringify(color));
    },
    [sendBridgeMessage]
  );

  const loadFullAppearance = useCallback(
    (appearance: AppearanceSettings) => {
      sendBridgeMessage('LoadFullAppearance', JSON.stringify(appearance));
    },
    [sendBridgeMessage]
  );

  const setCharacterType = useCallback(
    (characterClass: CharacterClass, gender: 'male' | 'female') => {
      const classIndex =
        characterClass === 'tank'
          ? 0
          : characterClass === 'archer'
          ? 1
          : characterClass === 'mage'
          ? 2
          : 3;
      sendBridgeMessage(
        'SetCharacterType',
        JSON.stringify({ characterClass: classIndex, gender })
      );
    },
    [sendBridgeMessage]
  );

  const setOutfit = useCallback(
    (outfitIndex: number) => {
      sendBridgeMessage('SetOutfit', String(outfitIndex));
    },
    [sendBridgeMessage]
  );

  const setHat = useCallback(
    (hatIndex: number) => {
      sendBridgeMessage('SetHat', String(hatIndex));
    },
    [sendBridgeMessage]
  );

  const setGlasses = useCallback(
    (glassesIndex: number) => {
      sendBridgeMessage('SetGlasses', String(glassesIndex));
    },
    [sendBridgeMessage]
  );

  const setBackpack = useCallback(
    (backpackIndex: number) => {
      sendBridgeMessage('SetBackpack', String(backpackIndex));
    },
    [sendBridgeMessage]
  );

  const setFacialHair = useCallback(
    (styleIndex: number) => {
      sendBridgeMessage('SetFacialHair', String(styleIndex));
    },
    [sendBridgeMessage]
  );

  // Granular part override (§4.3): {"slot":"torso","index":5}
  const setPart = useCallback(
    (slot: string, index: number) => {
      sendBridgeMessage('SetPart', JSON.stringify({ slot, index }));
    },
    [sendBridgeMessage]
  );

  // Morph sends are throttled ~50ms trailing PER KEY so slider drags don't
  // flood Unity — the latest value always wins.
  const morphTimersRef = useRef<Map<string, number>>(new Map());
  const morphPendingRef = useRef<Map<string, number>>(new Map());

  const setMorph = useCallback(
    (key: string, value: number) => {
      morphPendingRef.current.set(key, value);
      if (morphTimersRef.current.has(key)) return;
      const timerId = window.setTimeout(() => {
        morphTimersRef.current.delete(key);
        const pending = morphPendingRef.current.get(key);
        morphPendingRef.current.delete(key);
        if (pending !== undefined) {
          sendBridgeMessage('SetMorph', JSON.stringify({ key, value: pending }));
        }
      }, 50);
      morphTimersRef.current.set(key, timerId);
    },
    [sendBridgeMessage]
  );

  // Clear any pending morph timers on unmount.
  useEffect(() => {
    const timers = morphTimersRef.current;
    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const setCameraView = useCallback(
    (view: 'face' | 'body') => {
      sendBridgeMessage('SetCameraView', view);
    },
    [sendBridgeMessage]
  );

  const setPreviewRotation = useCallback(
    (degrees: number) => {
      sendBridgeMessage('SetPreviewRotation', String(Math.round(degrees)));
    },
    [sendBridgeMessage]
  );

  const playAnimation = useCallback(
    (animation: string) => {
      sendBridgeMessage('PlayAnimation', animation);
    },
    [sendBridgeMessage]
  );

  const setAnimationSpeed = useCallback(
    (speed: number) => {
      sendBridgeMessage('SetAnimationSpeed', String(speed));
    },
    [sendBridgeMessage]
  );

  const startShowcase = useCallback(
    () => {
      sendBridgeMessage('StartAnimationShowcase', '');
    },
    [sendBridgeMessage]
  );

  const stopShowcase = useCallback(
    () => {
      sendBridgeMessage('StopAnimationShowcase', '');
    },
    [sendBridgeMessage]
  );

  const resetToIdle = useCallback(
    () => {
      sendBridgeMessage('ResetToIdle', '');
    },
    [sendBridgeMessage]
  );

  return {
    unityProvider,
    isLoaded,
    isReady,
    loadingProgression,
    setGender,
    setCharacterClass,
    setOutfit,
    setHat,
    setGlasses,
    setBackpack,
    setFacialHair,
    setPart,
    setMorph,
    setCameraView,
    setPreviewRotation,
    updateSkinColor,
    updateHairStyle,
    updateHairColor,
    updateEyeColor,
    loadFullAppearance,
    setCharacterType,
    playAnimation,
    setAnimationSpeed,
    resetToIdle,
    startShowcase,
    stopShowcase,
  };
}
